// Inline SVG logo (the Jurion wordmark) — ink color matches --color-foreground
// (`#252525` per UI-SPEC §Color anchor). Stays in sync with /logo.svg in the
// repo root; rendering inline avoids an extra HTTP hop and lets us re-color
// via `currentColor` if needed in the future.
import type { SVGProps } from 'react';

export type LogoVariant = 'wordmark' | 'symbol';

export function Logo({
  className,
  variant = 'wordmark',
  ...props
}: SVGProps<SVGSVGElement> & { className?: string; variant?: LogoVariant }) {
  // Phase 128 (SHELL-05 / D-01) — mobile symbol-only variant. Renders ONLY the
  // mark group (the circle + interlaced wave path, the 1st inner <g> chain),
  // re-cropped to a near-square viewBox tight around the mark's rendered bounds
  // (computed from the transform chain: x≈[8.7,99.3], y≈[3.2,37.3] in outer svg
  // coords). Same `currentColor` ink, same `aria-label`, no new asset/HTTP hop.
  // The full-wordmark branch below is byte-for-byte the original — do NOT merge.
  if (variant === 'symbol') {
    // Intrinsic width/height attrs are intentionally omitted so the consumer's
    // `className` (e.g. `h-5 w-auto`) controls size without an iOS Safari
    // intrinsic-size override conflict (Phase 128 UI review).
    return (
      <svg viewBox="6 -28 96 96" role="img" aria-label="Jurion" className={className} {...props}>
        <g transform="translate(25.6,4.14609) scale(0.8,0.8)">
          <g fill="currentColor" fillRule="nonzero">
            <g transform="translate(-163.99472,-85.37608) scale(0.3989,0.3989)">
              <g>
                <g>
                  <circle cx="506.84" cy="294.04" r="23.93" />
                  <path d="M501.44,234.41h11.39c13.9,0 27.21,-5.01 38.14,-13.59c5.76,-4.52 13.11,-7.1 21.07,-6.76c16.08,0.69 29.32,13.55 30.43,29.61c1.29,18.65 -13.46,34.17 -31.83,34.17c-7.47,0 -14.34,-2.57 -19.77,-6.87c-11.01,-8.71 -24.6,-13.51 -38.64,-13.51h-11.39c-13.9,0 -27.21,5.01 -38.14,13.59c-5.76,4.52 -13.11,7.1 -21.07,6.76c-16.08,-0.69 -29.32,-13.55 -30.43,-29.61c-1.29,-18.65 13.46,-34.17 31.83,-34.17c7.47,0 14.34,2.57 19.77,6.87c11.01,8.71 24.6,13.51 38.64,13.51z" />
                </g>
              </g>
            </g>
          </g>
        </g>
      </svg>
    );
  }

  return (
    <svg
      viewBox="0 0 256 41.46094"
      width="256"
      height="41.46094"
      role="img"
      aria-label="Jurion"
      className={className}
      {...props}
    >
      <g transform="translate(25.6,4.14609) scale(0.8,0.8)">
        <g fill="currentColor" fillRule="nonzero">
          <g transform="translate(-163.99472,-85.37608) scale(0.3989,0.3989)">
            <g>
              <g>
                <circle cx="506.84" cy="294.04" r="23.93" />
                <path d="M501.44,234.41h11.39c13.9,0 27.21,-5.01 38.14,-13.59c5.76,-4.52 13.11,-7.1 21.07,-6.76c16.08,0.69 29.32,13.55 30.43,29.61c1.29,18.65 -13.46,34.17 -31.83,34.17c-7.47,0 -14.34,-2.57 -19.77,-6.87c-11.01,-8.71 -24.6,-13.51 -38.64,-13.51h-11.39c-13.9,0 -27.21,5.01 -38.14,13.59c-5.76,4.52 -13.11,7.1 -21.07,6.76c-16.08,-0.69 -29.32,-13.55 -30.43,-29.61c-1.29,-18.65 13.46,-34.17 31.83,-34.17c7.47,0 14.34,2.57 19.77,6.87c11.01,8.71 24.6,13.51 38.64,13.51z" />
                <path d="M655.97,280.96v-0.62h15.58v0.62c0,9.97 4.99,15.83 15.33,15.83c10.34,0 15.08,-5.86 15.08,-15.83v-52.6h15.96v52.6c0,18.57 -11.09,30.54 -31.16,30.54c-20.07,0 -30.79,-11.97 -30.79,-30.54z" />
                <path d="M730.52,280.34v-32.04h14.83v32.04c0,11.72 6.23,17.33 15.58,17.33c9.35,0 15.58,-5.61 15.58,-17.33v-32.04h14.83v32.04c0,20.57 -12.22,31.16 -30.42,31.16c-18.2,0 -30.42,-10.47 -30.42,-31.16z" />
                <path d="M803.57,274.1c0,-18.57 10.97,-27.42 27.05,-27.42c16.08,0 26.92,8.85 26.92,27.67v0.37h-14.71v-0.62c0,-9.97 -4.99,-13.59 -12.22,-13.59c-7.23,0 -12.22,3.61 -12.22,13.59v35.78h-14.83v-35.78z" />
                <path d="M865.27,230.47c0,-5.73 4.11,-9.97 9.85,-9.97c5.74,0 9.85,4.24 9.85,9.97c0,5.73 -4.11,9.85 -9.85,9.85c-5.74,0 -9.85,-4.11 -9.85,-9.85zM867.64,248.3h14.83v61.58h-14.83z" />
                <path d="M894.94,269.12c0,-24.43 17.95,-42.38 43.38,-42.38c25.43,0 43.38,17.95 43.38,42.38c0,24.43 -17.95,42.38 -43.38,42.38c-25.43,0 -43.38,-17.95 -43.38,-42.38zM965.5,269.12c0,-15.96 -10.97,-27.55 -27.18,-27.55c-16.21,0 -27.17,11.59 -27.17,27.55c0,15.96 10.97,27.67 27.17,27.67c16.2,0 27.18,-11.72 27.18,-27.67z" />
                <path d="M992.05,277.84c0,-20.57 12.22,-31.16 30.42,-31.16c18.2,0 30.42,10.47 30.42,31.16v32.04h-14.83v-32.04c0,-11.72 -6.23,-17.33 -15.58,-17.33c-9.35,0 -15.58,5.61 -15.58,17.33v32.04h-14.83v-32.04z" />
              </g>
            </g>
          </g>
        </g>
      </g>
    </svg>
  );
}
